import React, { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { DocumentTextIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useGetLocationPoliciesQuery, useCreatePolicyMutation } from '../../store/api/localApi';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useAuth } from '../../hooks/useAuth';
import { ScopedLink } from '../../components/ScopedLink';
import { PolicyStatusPill } from '../../components/policies/PolicyStatusPill';
import PolicyAudienceControl from '../../components/policies/PolicyAudienceControl';
import {
  CLASS_NOT_AT_LOCATION_MESSAGE,
  EVERYONE_AT_LOCATION,
} from '../../components/policies/audienceLabel';
import { PolicyAudience, PolicyChecklistKey, PolicyRegisterRow, PolicyStatus } from '../../types';

/**
 * The two slots that carry a written staff-facing duty (SI 575/2018's fire
 * safety policy definition; Children First Act 2015 s.11(5) for the
 * safeguarding statement). Mirrors the server-side default in
 * src/policies/handlers/create-policy — this only pre-ticks the box so the form
 * shows what will happen; the server still decides.
 */
const STAFF_ACK_DEFAULT_ON: readonly PolicyChecklistKey[] = [
  PolicyChecklistKey.FIRE_SAFETY,
  PolicyChecklistKey.CHILD_SAFEGUARDING_STATEMENT,
];

/**
 * The policy vault register — the module's front door and its inspection view.
 *
 * Renders every statutorily expected slot whether or not it is filled, in a
 * fixed order, so the page reads identically at every service. Empty slots are
 * shown NEUTRALLY: no red, no amber, no warning icon, no "X of 9 missing"
 * scoreboard. The vault is opt-in per policy, and a service that keeps
 * everything on paper must be able to open this page without being told off.
 *
 * The only count shown is a plain informational line, and it is phrased as
 * progress ("4 of 9 in the vault"), never as a deficit.
 */
const PolicyVault: React.FC = () => {
  // Same resolution order as the medication register: an explicit route param
  // (super-admin / org-admin scoped views) wins over the active context.
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { isParent, isAdmin } = useAuth();
  const { activeLocationId } = useLocationContext();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;

  const { data, isLoading } = useGetLocationPoliciesQuery(locationId ?? '', {
    skip: !locationId,
  });
  const [createPolicy, { isLoading: isCreating }] = useCreatePolicyMutation();
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customSummary, setCustomSummary] = useState('');
  const [customStaffAck, setCustomStaffAck] = useState(false);
  // `null` = "Chosen classes" with none ticked, which disables Create.
  const [customAudience, setCustomAudience] = useState<PolicyAudience | null>(EVERYONE_AT_LOCATION);
  const [customError, setCustomError] = useState<string | null>(null);
  // Which statutory slot this form is filling, if any. The per-row "Add"
  // buttons pre-filled the title but sent NO checklistKey, so filling a
  // statutory slot from the register silently created a CUSTOM policy: the
  // slot stayed empty ("Not added") forever and the register grew a duplicate
  // row with the same title. Tracking the key is what makes the row's Add
  // button actually fill its own slot.
  const [customChecklistKey, setCustomChecklistKey] = useState<PolicyChecklistKey | undefined>(
    undefined
  );

  // A parent who lands here (old link, bookmark) gets the ADMIN register:
  // every statutory slot for the location, with no way to sign or acknowledge
  // anything. It reads as a dead end. Send them to their own view, which is
  // where the sign/acknowledge affordances actually live.
  if (isParent) return <Navigate to="/parent/family/policies" replace />;

  const register: PolicyRegisterRow[] = data?.register ?? [];
  const checklistRows = register.filter((r) => !r.isCustom);
  const customRows = register.filter((r) => r.isCustom);
  const inVault = checklistRows.filter((r) => r.status === PolicyStatus.ACTIVE).length;

  const handleCreateCustom = async () => {
    if (!locationId || !customTitle.trim() || !customAudience) return;
    setCustomError(null);
    try {
      await createPolicy({
        locationId,
        title: customTitle.trim(),
        summary: customSummary.trim() || undefined,
        staffAckRequired: customStaffAck,
        // Undefined for a genuinely custom policy; set when the form was opened
        // from a statutory row, so the slot is filled rather than duplicated.
        checklistKey: customChecklistKey,
        audience: customAudience,
      }).unwrap();
    } catch (err) {
      const data = (err as { data?: { code?: string; error?: string } })?.data;
      setCustomError(
        data?.code === 'CLASS_NOT_AT_LOCATION'
          ? CLASS_NOT_AT_LOCATION_MESSAGE
          : data?.error || 'Could not create the policy. Try again.'
      );
      return;
    }
    setCustomTitle('');
    setCustomSummary('');
    setCustomStaffAck(false);
    setCustomChecklistKey(undefined);
    setCustomAudience(EVERYONE_AT_LOCATION);
    setShowCustomForm(false);
  };

  if (isLoading) {
    return (
      <div data-testid="policy-vault-loading" className="py-10 text-center text-sm text-gray-500">
        Loading the policy vault…
      </div>
    );
  }

  return (
    <div data-testid="policy-vault" className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Policy Vault</h1>
          <p className="mt-1 text-sm text-gray-600">
            The policies and statements a school-age service is expected to hold. Add them as you go
            — nothing here is required to use the rest of the app.
          </p>
        </div>
        {/*
          Creating a policy is an ADMIN act — POST /locations/{id}/policies
          returns 403 for STAFF. Staff legitimately read this register and
          acknowledge what they are asked to read, but showing them an "Add"
          button they can only fail with is a dead end, not a permission
          boundary. The backend is the boundary; this just stops offering an
          action that cannot succeed.
        */}
        {isAdmin && (
          <button
            type="button"
            data-testid="policy-add-custom"
            onClick={() => {
              // Clear any slot left over from a row's "Add" click — otherwise
              // the page-level button would silently fill that statutory slot
              // with a policy the admin meant to be custom.
              setCustomChecklistKey(undefined);
              setCustomStaffAck(false);
              setCustomTitle('');
              setCustomAudience(EVERYONE_AT_LOCATION);
              setCustomError(null);
              setShowCustomForm((v) => !v);
            }}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 sm:mt-0"
          >
            <PlusIcon className="h-4 w-4" />
            Add a policy
          </button>
        )}
      </div>

      {/*
        Informational only. Phrased as progress, never as a deficit, and
        carries no alarm styling — see the e2e, which asserts no element with
        a warning testid exists on this page.
      */}
      <p data-testid="policy-vault-progress" className="text-sm text-gray-500">
        {inVault} of {checklistRows.length} suggested policies are in the vault.
      </p>

      {showCustomForm && (
        <div
          data-testid="policy-custom-form"
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <label className="block text-sm font-medium text-gray-700" htmlFor="policy-custom-title">
            Policy title
          </label>
          <input
            id="policy-custom-title"
            data-testid="policy-custom-title"
            value={customTitle}
            onChange={(e) => setCustomTitle(e.target.value)}
            placeholder="e.g. Healthy Eating Policy"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />
          <label
            className="mt-3 block text-sm font-medium text-gray-700"
            htmlFor="policy-custom-summary"
          >
            Short summary (optional)
          </label>
          <input
            id="policy-custom-summary"
            data-testid="policy-custom-summary"
            value={customSummary}
            onChange={(e) => setCustomSummary(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
          />

          <label className="mt-4 flex items-start gap-2">
            <input
              type="checkbox"
              data-testid="policy-custom-staff-ack"
              checked={customStaffAck}
              onChange={(e) => setCustomStaffAck(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            <span className="text-sm text-gray-700">Staff must acknowledge this policy</span>
          </label>

          {/*
            parentAction cannot be set here and saying otherwise would be a lie:
            a new policy is created with no version, and the parent-facing ask
            lives on the version. Until a document is uploaded there is nothing
            for a parent to be shown.
          */}
          <p data-testid="policy-custom-parent-note" className="mt-2 text-xs text-gray-500">
            You will choose what parents are asked — nothing, read it, confirm it, or sign it — when
            you upload the document.
          </p>

          {locationId && (
            <div className="mt-4">
              <PolicyAudienceControl
                locationId={locationId}
                value={customAudience}
                onChange={setCustomAudience}
              />
            </div>
          )}

          {customError && (
            <p data-testid="policy-custom-error" className="mt-3 text-sm text-red-600">
              {customError}
            </p>
          )}

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              data-testid="policy-custom-save"
              disabled={!customTitle.trim() || !customAudience || isCreating}
              onClick={handleCreateCustom}
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              Create
            </button>
            <button
              type="button"
              data-testid="policy-custom-cancel"
              onClick={() => setShowCustomForm(false)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <ul className="divide-y divide-gray-200">
          {register.map((row) => (
            <li
              key={row.policyId ?? row.checklistKey}
              data-testid="policy-register-row"
              data-checklist-key={row.checklistKey ?? ''}
              // Custom policies have no checklist key, so their "Open" testid
              // is built from a policyId that regenerates on every reseed. The
              // title is the only stable handle a test can hold.
              data-policy-title={row.title}
              className="flex items-center justify-between gap-4 px-4 py-4"
            >
              <div className="flex min-w-0 items-start gap-3">
                <DocumentTextIcon
                  aria-hidden="true"
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400"
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{row.title}</p>
                  {/* Neutral, like everything else on the register: a policy
                      for chosen classes is a fact about it, not a problem. */}
                  {row.policyId && row.audience?.scope === 'CLASSES' && (
                    <span
                      data-testid={`policy-audience-chip-${row.policyId}`}
                      className="mt-1 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                    >
                      Chosen classes
                    </span>
                  )}
                  {row.paperNote && (
                    <p data-testid="policy-paper-note" className="mt-0.5 text-xs text-gray-500">
                      {row.paperNote}
                    </p>
                  )}
                  {row.reviewDueAt && (
                    <p data-testid="policy-review-due" className="mt-0.5 text-xs text-gray-500">
                      Review due {row.reviewDueAt}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-shrink-0 items-center gap-3">
                <PolicyStatusPill status={row.status} />
                {/*
                  Open vs Add is about DOCUMENT presence, not whether a policy
                  ROW happens to exist. A NOT_ADDED slot has no document to open
                  either way — whether an empty shell row was created earlier or
                  the slot is a pure suggestion is an internal detail the admin
                  should not have to reason about. So every document-less slot
                  reads "Add document"; only a slot that is actually in the vault
                  (a published document, or held on paper) reads "Open".

                  The two destinations still differ: a slot with a row opens its
                  detail page (which now carries the upload panel); a slot with
                  no row opens the inline create form, pre-filled — because
                  /policies/new is not a real route.
                */}
                {row.status !== PolicyStatus.NOT_ADDED && row.policyId ? (
                  <ScopedLink
                    to={`/policies/${row.policyId}`}
                    data-testid={`policy-open-${row.checklistKey ?? row.policyId}`}
                    className="text-sm font-medium text-primary-700 hover:text-primary-800"
                  >
                    Open
                  </ScopedLink>
                ) : !isAdmin ? null : row.policyId ? (
                  // Empty slot that already has a shell row: open its detail
                  // page, where the new upload panel lives.
                  <ScopedLink
                    to={`/policies/${row.policyId}`}
                    data-testid={`policy-adddoc-${row.checklistKey ?? row.policyId}`}
                    className="text-sm font-medium text-primary-700 hover:text-primary-800"
                  >
                    Add document
                  </ScopedLink>
                ) : (
                  // Pure suggestion, no row yet. The page-level "Add a policy"
                  // form already works, so open THAT pre-filled with this row's
                  // title rather than inventing a /policies/new screen.
                  <button
                    type="button"
                    data-testid={`policy-add-${row.checklistKey}`}
                    onClick={() => {
                      setCustomTitle(row.title);
                      setCustomStaffAck(
                        !!row.checklistKey && STAFF_ACK_DEFAULT_ON.includes(row.checklistKey)
                      );
                      setCustomChecklistKey(row.checklistKey);
                      setCustomAudience(EVERYONE_AT_LOCATION);
                      setCustomError(null);
                      setShowCustomForm(true);
                      // The form renders above the register; bring it into view
                      // so the click doesn't appear to do nothing on a long list.
                      requestAnimationFrame(() => {
                        document
                          .querySelector('[data-testid="policy-custom-form"]')
                          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      });
                    }}
                    className="text-sm font-medium text-primary-700 hover:text-primary-800"
                  >
                    Add document
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {customRows.length === 0 && (
        <p className="text-xs text-gray-500">
          You can also add your own policies — Healthy Eating, Outings, GDPR, anything your service
          holds.
        </p>
      )}
    </div>
  );
};

export default PolicyVault;
